import { copyToClipboard } from '@thoughtspot/browser-util';
import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import { Alert, BannerAlertType, } from '@thoughtspot/radiant-react/widgets/alert';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { Horizontal, Vertical, View, } from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { Modal, ModalSize } from '@thoughtspot/radiant-react/widgets/modal';
import { Colors, Typography, } from '@thoughtspot/radiant-react/widgets/typography';
import { getAccessToken, getCurrentOrgId, getScimBaseUrl, getUserPrivileges, isScimPerOrgEnabled, } from '@thoughtspot/session-service';
import { getIdpDomain } from '@thoughtspot/system-config-service';
import React, { useCallback, useEffect, useState } from 'react';
import { useCreateScimTokenMutation, useGetScimAuthTokenLazyQuery, } from '../../../services/saml/saml-service';
import { Header } from '../../system/common';
import { LearnMore, Mode } from '../../system/common/constants';
import { TranslationsKeys } from '../constants';
import styles from './provisioning.module.scss';
import { useProvisioningMutation } from './useProvisioningCustomHook';
export const Provisioning = () => {
    const [screenLoader, setScreenLoader] = useState(false);
    const [token, setToken] = useState('');
    const [nonce, setNonce] = useState('');
    const [isCopyDisabled, setIsCopyDisabled] = useState(true);
    const [errorMessage, setErrorMessage] = useState();
    const [showModal, setShowModal] = useState(false);
    const [isToastAlertVisible, setIsToastAlertVisible] = useState();
    const [toastMessage, setToastMessage] = useState();
    const [showErrorBanner, setShowErrorBanner] = useState(false);
    const [copied, setCopied] = useState(false);
    const [renderToken, setRenderToken] = useState(false);
    const { t } = useTranslation();
    const getTranslations = (key, args) => t(`${key}`, args);
    const copyUrl = () => {
        copyToClipboard(`${getScimBaseUrl()}/scim/v2/`, () => {
            setIsToastAlertVisible(true);
            setToastMessage(t(TranslationsKeys.PROVISIONING_COPIED_SUCCESS));
        });
    };
    const getDefaultScimHeaders = () => {
        return {
            accessToken: `Bearer ${getAccessToken()}`,
            xTsPrivileges: btoa(JSON.stringify(getUserPrivileges())),
            xTsIdpDomain: getIdpDomain(),
        };
    };
    const copyToken = useCallback((token) => {
        copyToClipboard(token, () => {
            setIsToastAlertVisible(true);
            setToastMessage(t(TranslationsKeys.PROVISIONING_COPIED_SUCCESS));
        });
        setCopied(true);
    }, [isToastAlertVisible, toastMessage, token]);
    const { createScimToken, error, data, loading, } = useCreateScimTokenMutation();
    const { fetchSignedToken: createSignedScimToken, error: authError, data: authData, loading: authLoading, } = useProvisioningMutation();
    const { getScimToken, error: getScimTokenError, ScimTokenAuthData, loading: getScimTokenLoading, } = useGetScimAuthTokenLazyQuery();
    const generateSignedToken = (nonce) => {
        createSignedScimToken(window.location.hostname, nonce, `${getCurrentOrgId()}`, getDefaultScimHeaders(), isScimPerOrgEnabled(), getScimBaseUrl());
        setScreenLoader(true);
    };
    useEffect(() => {
        if (!getScimTokenLoading && (ScimTokenAuthData || getScimTokenError)) {
            setScreenLoader(false);
            if (getScimTokenError) {
                setErrorMessage(t(TranslationsKeys.PROVISIONING_FETCH_ERROR));
                setShowErrorBanner(true);
            }
            else {
                setIsCopyDisabled(false);
                setNonce(ScimTokenAuthData?.token);
            }
        }
    }, [ScimTokenAuthData, getScimTokenError, getScimTokenLoading]);
    useEffect(() => {
        if (!loading && (data || error)) {
            if (error) {
                setScreenLoader(false);
                setErrorMessage(t(TranslationsKeys.PROVISIONING_GENERATE_ERROR));
                setShowErrorBanner(true);
            }
            else {
                setIsToastAlertVisible(true);
                setToastMessage(t('scim.generate.success.message'));
                setIsCopyDisabled(false);
                setShowErrorBanner(false);
                setNonce(data?.Data?.token);
                generateSignedToken(data?.Data?.token);
            }
        }
    }, [data, error, loading]);
    useEffect(() => {
        if (!authLoading && (authData || authError)) {
            setScreenLoader(false);
            if (authError) {
                setErrorMessage(t(TranslationsKeys.PROVISIONING_GENERATE_ERROR));
                setShowErrorBanner(true);
            }
            else {
                setIsToastAlertVisible(true);
                setToastMessage(t('scim.generate.success.message'));
                setIsCopyDisabled(false);
                setShowErrorBanner(false);
                setToken(authData?.token);
            }
        }
    }, [authData, authError, authLoading]);
    useEffect(() => {
        setScreenLoader(true);
        getScimToken('primary');
    }, []);
    useEffect(() => {
        if (token.length !== 0) {
            setRenderToken(false);
            setTimeout(() => {
                setRenderToken(true);
            });
        }
    }, [token]);
    const textInputBox = () => {
        return (React.createElement(View, { className: styles.container },
            React.createElement(Typography, { variant: "p", className: styles.scimUrl }, `${getScimBaseUrl()}/scim/v2/`),
            React.createElement(Button, { icon: IconID.COPY, className: styles.copyButton, size: "s", type: "secondary", onClick: copyUrl })));
    };
    const generateToken = () => {
        if (nonce !== undefined && nonce !== null && nonce.length !== 0) {
            setShowModal(true);
            return;
        }
        setScreenLoader(true);
        createScimToken('primary');
    };
    const getLabel = (label) => (React.createElement(View, { className: styles.inputLabel }, label));
    const provisioningFormBuilder = () => {
        return (React.createElement(Vertical, { spacing: "e" },
            React.createElement(Horizontal, { spacing: "e" },
                React.createElement(Vertical, { spacing: "a", className: styles.labelContainer },
                    getLabel(t(TranslationsKeys.PROVISIONING_BASE_URL)),
                    React.createElement(Typography, { variant: "caption", className: styles.hint }, t('scim.baseurl.info'))),
                textInputBox()),
            React.createElement(Horizontal, { spacing: "e" },
                React.createElement(Vertical, { spacing: "a", className: styles.labelContainer },
                    getLabel(t(TranslationsKeys.PROVISIOINING_ACTION_TITLE)),
                    React.createElement(Typography, { variant: "caption", className: styles.hint }, t('scim.genrate.info'))),
                React.createElement(Horizontal, { className: styles.buttonWrap, spacing: "a" },
                    React.createElement(Button, { isDisabled: false, text: getTranslations(TranslationsKeys.PROVISIOINING_GENERATE_BUTTON), onClick: generateToken, "data-testid": "genrate-button" })))));
    };
    const tokenCopyContainerBuilder = () => {
        return (React.createElement(Vertical, { className: styles.tokenContainer },
            React.createElement(Horizontal, { className: styles.copyRow },
                React.createElement(Button, { icon: IconID.COPY, className: styles.copyButton, size: "s", type: "secondary", onClick: () => {
                        copyToken(token);
                    } }),
                copied && (React.createElement(Typography, { variant: "p-small", className: styles.copiedText }, getTranslations('saml.ThoughtSpotToIDP.Copied.Msg')))),
            React.createElement(Vertical, { className: styles.tokenText }, token)));
    };
    return (React.createElement(LoadingIndicator.Global, { hideLoader: !screenLoader, loadingText: t(TranslationsKeys.PROVISIONING_AUTH_LOADING_TEXT), className: `${styles.loadIndicator} ${screenLoader ? styles.disallowScroll : ''}`, "data-testid": "scim-provier-loader" },
        React.createElement(Modal, { isOpen: showModal, size: ModalSize.SMALL, title: t(TranslationsKeys.PROVISIOINING_AUTH_TITLE), confirmButton: {
                text: t(TranslationsKeys.PROVISIONING_AUTH_MODAL_MESSAGE),
                onConfirm: () => {
                    setScreenLoader(true);
                    createScimToken('primary');
                    setShowModal(false);
                },
            }, dismissButton: {
                text: t(TranslationsKeys.PROVISIONING_AUTH_GENERATE_CANCEL),
            }, onDismiss: () => {
                setShowModal(false);
            } },
            React.createElement(Vertical, null,
                React.createElement(Typography, { "data-testid": "scim-generate-dialog", variant: "body-normal", color: Colors.gray, className: styles.description }, t(TranslationsKeys.PROVISIONING_AUTH_GENERATE_WARNING)))),
        showErrorBanner && (React.createElement(Alert.Banner, { message: errorMessage, isVisible: true, type: BannerAlertType.ERROR, onClose: () => {
                setShowErrorBanner(false);
            } })),
        React.createElement(Vertical, { className: styles.provisioningContainer },
            React.createElement(Header, { title: t(TranslationsKeys.PROVISIONING_HEADER), subTitle: t(TranslationsKeys.PROVISIONING_HEADING_SUBTITLE), titleSize: "large", learnMoreKey: LearnMore.INTERNAL_AUTH, mode: Mode.VIEW, actions: undefined }),
            React.createElement(Typography, { variant: "h3", className: styles.formHeading }, getTranslations(TranslationsKeys.PROVISIONING_FORM_TITLE)),
            provisioningFormBuilder(),
            renderToken && tokenCopyContainerBuilder()),
        React.createElement(GlobalAlert.Toast, { "data-testid": "toast-message-scim", message: toastMessage, isVisible: isToastAlertVisible, onClose: () => setIsToastAlertVisible(false), autoHide: true })));
};
//# sourceMappingURL=provisioning.container.js.map